"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.askedQuestionsOfOutput = askedQuestionsOfOutput;
exports.answerText = answerText;
exports.isAskUser = isAskUser;
exports.questionsOfCall = questionsOfCall;
exports.askedQuestionsIn = askedQuestionsIn;
exports.questionsAskedBy = questionsAskedBy;
exports.closeOpenQuestions = closeOpenQuestions;
const zod_1 = require("zod");
const questions_1 = require("./questions");
const askedQuestionsSchema = zod_1.z.array(zod_1.z.object({ question: zod_1.z.string(), answer: zod_1.z.string().optional() }));
/**
 * What a tool that asked about its own call says it asked, from its output's `askedQuestions`. The
 * ask_user tool's questions are its input instead.
 */
function askedQuestionsOfOutput(output) {
    const asked = output?.askedQuestions;
    if (!Array.isArray(asked) || asked.length === 0)
        return undefined;
    const parsed = askedQuestionsSchema.safeParse(asked);
    return parsed.success ? parsed.data : undefined;
}
function answerText(answer) {
    return [...answer.selected, ...(answer.other ? [answer.other] : [])].join(', ');
}
/** Whether a thread part is an ask_user call, typed or known to the client only by name. */
function isAskUser(part) {
    const call = part;
    return (call.type === `tool-${questions_1.ASK_USER_TOOL_NAME}` ||
        (call.type === 'dynamic-tool' && call.toolName === questions_1.ASK_USER_TOOL_NAME));
}
/**
 * The questions a pending call asks, when it is a question. By input as well as name: a connected
 * server can offer a tool called ask_user too, and one whose input does not read as questions stays
 * an ordinary approval, which can still be denied.
 */
function questionsOfCall(toolName, input) {
    if (toolName !== questions_1.ASK_USER_TOOL_NAME)
        return undefined;
    const parsed = questions_1.askUserInputSchema.safeParse(input);
    return parsed.success ? parsed.data.questions : undefined;
}
/**
 * What a finished ask_user call in a thread asked and what came back: the answers once it
 * completes, the bare questions when the person skipped them or moved on. Nothing while the call
 * still waits, since its questions are on screen to answer.
 */
function askedQuestionsIn(part) {
    const call = part;
    if (!isAskUser(call)) {
        return call.state === 'output-available' ? askedQuestionsOfOutput(call.output) : undefined;
    }
    if (call.state === 'output-available') {
        const parsed = questions_1.askUserAnswersSchema.safeParse(call.output);
        return parsed.success
            ? parsed.data.answers.map((answer) => ({
                question: answer.question,
                answer: answerText(answer),
            }))
            : undefined;
    }
    if (call.state !== 'output-error' && call.state !== 'output-denied')
        return undefined;
    return questionsAskedBy(call.input);
}
/** The questions an ask_user call's input asks, none of them answered. */
function questionsAskedBy(input) {
    const parsed = questions_1.askUserInputSchema.safeParse(input);
    return parsed.success ? parsed.data.questions.map(({ question }) => ({ question })) : undefined;
}
/**
 * The thread with every ask_user call still waiting closed as not answered, for a person who sent
 * a new message instead. Closed as an error rather than a declined approval: the SDK acts on an
 * approval response in a resent transcript as if it were new, streaming an outcome for a call the
 * new reply never made. A host that knows other calls waited on questions, such as a tool that
 * asked about its own call, says which with `asks`.
 */
function closeOpenQuestions(messages, asks = isAskUser) {
    const isOpen = (part) => asks(part) && part.state === 'approval-requested';
    return messages.map((message) => {
        if (!message.parts.some(isOpen))
            return message;
        return {
            ...message,
            parts: message.parts.map((part) => {
                if (!isOpen(part))
                    return part;
                const closed = {
                    ...part,
                    state: 'output-error',
                    errorText: questions_1.NOT_ANSWERED_REASON,
                };
                delete closed.approval;
                return closed;
            }),
        };
    });
}
